import * as THREE from 'three';
import type { Frame } from '../world/Spline';
import { damp, clamp } from '../util/math';

const WORLD_UP = new THREE.Vector3(0, 1, 0);

/**
 * Damped chase cam (§6): behind & above the ball, pulls back and widens FOV with
 * speed, leads slightly into turns. Touch uses auto-follow only.
 */
export class ChaseCamera {
  readonly camera: THREE.PerspectiveCamera;

  private travelDir = new THREE.Vector3(0, 0, -1);
  private lookTarget = new THREE.Vector3();
  private yawNudge = 0;
  private shake = 0;

  // Tunables (§12.7).
  private baseDist = 8.5;
  private speedDist = 6;
  private baseHeight = 4.2;
  private lookAhead = 7;
  private baseFov = 62;
  private speedFov = 16;

  constructor(aspect: number) {
    this.camera = new THREE.PerspectiveCamera(this.baseFov, aspect, 0.1, 4000);
  }

  resize(aspect: number): void {
    this.camera.aspect = aspect;
    this.camera.updateProjectionMatrix();
  }

  /** Snap behind the ball without damping (start / respawn). */
  snap(ballPos: THREE.Vector3, frame: Frame): void {
    this.travelDir.copy(frame.tangent);
    this.travelDir.y = 0;
    if (this.travelDir.lengthSq() < 1e-4) this.travelDir.set(0, 0, -1);
    this.travelDir.normalize();
    const pos = ballPos
      .clone()
      .addScaledVector(this.travelDir, -this.baseDist)
      .addScaledVector(WORLD_UP, this.baseHeight);
    this.camera.position.copy(pos);
    this.lookTarget.copy(ballPos);
    this.camera.lookAt(this.lookTarget);
  }

  addShake(amount: number): void {
    this.shake = Math.min(1, this.shake + amount);
  }

  update(
    dt: number,
    ballPos: THREE.Vector3,
    velocity: THREE.Vector3,
    speed01: number,
    frame: Frame,
    nudge: { x: number; y: number },
    resolve?: (camPos: THREE.Vector3, ballPos: THREE.Vector3) => void,
  ): void {
    // Smooth the travel direction from velocity (fall back to track tangent).
    const horiz = velocity.clone();
    horiz.y = 0;
    let dirTarget = frame.tangent.clone();
    dirTarget.y = 0;
    if (horiz.lengthSq() > 1) dirTarget = horiz.normalize();
    else dirTarget.normalize();
    this.travelDir.lerp(dirTarget, damp(0, 1, 4, dt)).normalize();

    // Camera nudge orbits the view a little (desktop only; auto-follow on touch).
    this.yawNudge = damp(this.yawNudge, clamp(nudge.x, -1.2, 1.2), 8, dt);
    const orbited = this.travelDir.clone().applyAxisAngle(WORLD_UP, this.yawNudge * 0.6);

    const dist = this.baseDist + this.speedDist * speed01;
    const height = this.baseHeight + speed01 * 1.5;
    const desired = ballPos
      .clone()
      .addScaledVector(orbited, -dist)
      .addScaledVector(WORLD_UP, height);

    this.camera.position.lerp(desired, damp(0, 1, 6, dt));

    // Lead into the turn: look ahead along travel, biased toward the track ahead.
    const lead = ballPos
      .clone()
      .addScaledVector(this.travelDir, this.lookAhead)
      .addScaledVector(WORLD_UP, 1.2);
    this.lookTarget.lerp(lead, damp(0, 1, 7, dt));

    // Shake decays.
    if (this.shake > 0) {
      const s = this.shake * 0.6;
      this.camera.position.x += (Math.random() - 0.5) * s;
      this.camera.position.y += (Math.random() - 0.5) * s;
      this.camera.position.z += (Math.random() - 0.5) * s;
      this.shake = Math.max(0, this.shake - dt * 3);
    }

    // Keep the camera out of geometry (walls/hills) and above the track.
    resolve?.(this.camera.position, ballPos);

    this.camera.lookAt(this.lookTarget);

    const fov = this.baseFov + this.speedFov * speed01;
    if (Math.abs(this.camera.fov - fov) > 0.05) {
      this.camera.fov = damp(this.camera.fov, fov, 4, dt);
      this.camera.updateProjectionMatrix();
    }
  }
}
